import { Directive, computed, inject, input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { TFS_ICON_REGISTRY, TfsIconName } from './icon-registry';

/**
 * Renders a Phosphor icon inside an Angular Material `<mat-icon>` host. Reusing
 * Material's icon shell means every Material context (button leading icon,
 * menu-item icon, list-item icon, chip avatar, form-field suffix, badge host)
 * gets the right spacing, sizing and alignment automatically — Material's CSS
 * already handles `.mat-icon` placement.
 *
 * @example
 * ```html
 * <mat-icon tfsIcon="gear" />
 * <mat-icon [tfsIcon]="isOpen() ? 'caret-left' : 'caret-right'" />
 * <button mat-flat-button>
 *   <mat-icon tfsIcon="plus" /> Add Tools
 * </button>
 * <button mat-icon-button>
 *   <mat-icon tfsIcon="x" aria-label="Close" />
 * </button>
 * ```
 *
 * Add a new icon by importing the `phosphorXxx` constant from
 * `@ng-icons/phosphor-icons/regular` and registering it in `icon-registry.ts`.
 */
@Directive({
  selector: 'mat-icon[tfsIcon]',
  host: {
    '[innerHTML]': 'svg()',
    '[attr.data-tfs-icon]': 'tfsIcon()',
  },
})
export class TfsIconDirective {
  private readonly sanitizer = inject(DomSanitizer);

  readonly tfsIcon = input.required<TfsIconName>();

  protected readonly svg = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(TFS_ICON_REGISTRY[this.tfsIcon()])
  );
}
